// Lesson page and replacement — the core feature (SPEC §7.4).

import { fetchClassrooms } from "../../api/classrooms";
import type { ApiResult } from "../../api/client";
import { assignSubstitute, fetchCandidates, fetchLesson, fetchLessonHistory } from "../../api/lessons";
import type { Candidates, ChangeLogEntry, Classroom, Lesson, LessonKey } from "../../api/types";
import { t } from "../../i18n/i18n";
import { setRedraw, type RouteMatch } from "../../router";
import { isAdmin } from "../../session";
import { buildConflictList } from "../../ui/conflictList";
import { createButton, createElement } from "../../ui/dom";
import { describeProblem, showToast } from "../../ui/feedback";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import { buildCandidatesSection } from "./candidatesTable";
import { buildLessonActions, type ActionHandlers } from "./lessonActions";
import { buildLessonHeader } from "./lessonHeader";
import { buildLessonHistory } from "./lessonHistory";
import type { LessonAction, LessonPageState } from "./lessonState";

export async function renderLessonPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const key: LessonKey = {
    groupId: route.params.groupId ?? "",
    date: route.params.date ?? "",
    slotId: route.params.slotId ?? "",
  };
  const page: HTMLElement = startPage(container, t("lesson.title"));
  page.append(createLoading());
  const state: LessonPageState | null = await loadState(key, page);
  if (state === null) {
    return;
  }

  const draw = (): void => {
    drawPage(container, state);
  };
  setRedraw(container, draw);
  draw();
}

async function loadState(key: LessonKey, page: HTMLElement): Promise<LessonPageState | null> {
  const lesson: ApiResult<Lesson> = await fetchLesson(key);
  if (lesson.ok === false) {
    showPageProblem(page, lesson.problem);
    return null;
  }

  const state: LessonPageState = {
    key, lesson: lesson.data, candidates: null, history: [], classrooms: [], openEditor: null,
    showExcluded: false, conflict: null, problem: null,
  };
  await reloadDetails(state);
  return state;
}

/** Candidates (not for cancelled lessons), history and classrooms for the room picker. */
async function reloadDetails(state: LessonPageState): Promise<void> {
  const candidates: ApiResult<Candidates> | null = state.lesson.isCancelled ? null : await fetchCandidates(state.key);
  const history: ApiResult<ChangeLogEntry[]> = await fetchLessonHistory(state.key);
  const classrooms: ApiResult<Classroom[]> = await fetchClassrooms(false);
  state.candidates = candidates?.ok === true ? candidates.data : null;
  state.history = history.ok ? history.data : [];
  state.classrooms = classrooms.ok ? classrooms.data : [];
}

function drawPage(container: HTMLElement, state: LessonPageState): void {
  const title: string = `${state.lesson.groupName} — ${t("lesson.title")}`;
  const page: HTMLElement = startPage(container, title);
  page.append(buildLessonHeader(state.lesson));
  appendMessages(page, container, state);
  if (isAdmin()) {
    page.append(buildLessonActions(state, createHandlers(container, state)));
  }

  appendCandidates(page, container, state);
  page.append(buildLessonHistory(state.history));
}

function appendMessages(page: HTMLElement, container: HTMLElement, state: LessonPageState): void {
  if (state.conflict !== null) {
    page.append(buildConflictList(state.conflict.conflicts, state.conflict.retry));
  }

  if (state.problem !== null) {
    const box: HTMLDivElement = createElement("div", "error-box", describeProblem(state.problem));
    box.setAttribute("role", "alert");
    if (state.problem.code === "CONCURRENT_EDIT") {
      box.append(" ", createButton(t("app.reload"), (): Promise<void> => reloadAll(container, state)));
    }

    page.append(box);
  }
}

function appendCandidates(page: HTMLElement, container: HTMLElement, state: LessonPageState): void {
  if (state.lesson.isCenterClosed) {
    page.append(createElement("div", "banner", t("lesson.closedNoCandidates")));
    return;
  }

  if (state.candidates === null) {
    return;
  }

  page.append(buildCandidatesSection({
    candidates: state.candidates,
    date: state.key.date,
    showExcluded: state.showExcluded,
    onToggleExcluded: (): void => {
      state.showExcluded = state.showExcluded === false;
      drawPage(container, state);
    },
    onAssign: isAdmin() ? (teacherId: string): Promise<void> => runAction(container, state,
      (confirm: boolean) => assignSubstitute(state.key, teacherId, state.lesson.changeVersion, confirm))
      : null,
  }));
}

function createHandlers(container: HTMLElement, state: LessonPageState): ActionHandlers {
  return {
    perform: (action: LessonAction): Promise<void> => runAction(container, state, action),
    toggleEditor: (editor: LessonPageState["openEditor"]): void => {
      state.openEditor = state.openEditor === editor ? null : editor;
      drawPage(container, state);
    },
  };
}

/** Runs an action; conflicts offer "Save anyway", which repeats it with confirm (SPEC §6.1). */
async function runAction(container: HTMLElement, state: LessonPageState, action: LessonAction): Promise<void> {
  const result: ApiResult<Lesson> = await action(false);
  if (result.ok) {
    await finishAction(container, state, result.data);
    return;
  }

  state.conflict = null;
  state.problem = result.problem;
  if (result.problem.code === "SCHEDULE_CONFLICTS") {
    state.problem = null;
    state.conflict = { conflicts: result.problem.conflicts, retry: (): Promise<void> => confirmAction(container,
      state, action) };
  }

  drawPage(container, state);
}

async function confirmAction(container: HTMLElement, state: LessonPageState, action: LessonAction): Promise<void> {
  const result: ApiResult<Lesson> = await action(true);
  state.conflict = null;
  if (result.ok) {
    await finishAction(container, state, result.data);
    return;
  }

  state.problem = result.problem;
  drawPage(container, state);
}

async function finishAction(container: HTMLElement, state: LessonPageState, lesson: Lesson): Promise<void> {
  state.lesson = lesson;
  state.openEditor = null;
  state.conflict = null;
  state.problem = null;
  await reloadDetails(state);
  drawPage(container, state);
  showToast(t("app.saved"));
}

async function reloadAll(container: HTMLElement, state: LessonPageState): Promise<void> {
  const lesson: ApiResult<Lesson> = await fetchLesson(state.key);
  if (lesson.ok) {
    await finishAction(container, state, lesson.data);
  }
}
